import {
  APP_LINK_ADJUST,
  APP_SCHEME_BASE,
  BASE_URL,
} from '../constants/constants';
import {
  DatasetProps,
  FType,
  PayloadFetchOrder,
  RecordProps,
  ResponseFetchOrder,
  APIVersions
} from "../types/types";

export const isIOS =
  /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const buildPayload = (
  checkOutElement: DatasetProps,
  flowType: FType,
): PayloadFetchOrder => {
  const {
    apiKey,
    sellerSessionId,
    cartId,
    point,
    totalAmount,
    productName,
    productQuantity,
    sellerChannel,
    additionalData = '{}',
    callbackUrl,
    callbackUrlSuccess,
    callbackUrlFail
  } = checkOutElement;

  return {
    apiKey,
    sellerSessionId,
    sellerChannel: sellerChannel,
    flowType,
    cartId,
    orderData: {
      point,
      totalAmount: Number(totalAmount),
      productName: productName,
      productQuantity: Number(productQuantity),
      additionalData: { ...JSON.parse(additionalData) },
    },
      callbackUrl,
      callbackUrlSuccess,
      callbackUrlFail
  };
};

export const checkDataset = (input: { [index: string]: string }): boolean => {
  const schema: Record<keyof RecordProps, string> = {
    cartId: "string",
    point: "string",
    sellerSessionId: "string",
    totalAmount: "string",
    callbackUrl: 'string',
    callbackUrlFail: 'string',
    callbackUrlSuccess: 'string',
  };

  const missingProperties: string[] = Object.keys(schema)
    .filter((key) => !input[key])
    .map((key) => {
      if (input[key] === undefined)
        console.error(`SDK Personal Pay: missing property ${key}`);
      if (!input[key]?.length)
        console.error(`SDK Personal Pay: missing value in property ${key}`);
      return key;
    });

  const hasAuth = input.apiKey || input.accessToken;
  if (!hasAuth) {
    console.error('SDK Personal Pay: missing either "data-api-key" or "data-access-token"');
    return false;
  }

  return !missingProperties.length;
};

export const parseFlowUrl = (urlString: string | URL) => {
  const url = new URL(urlString);
  const params = url.searchParams;

  let adjParams: URLSearchParams | null = null;

  const adjRedirectRaw = params.get("adj_redirect");

  // 👉 Solo entra acá si es URL de Adjust
  if (adjRedirectRaw) {
    try {
      const decoded = decodeURIComponent(adjRedirectRaw);
      adjParams = new URL(decoded).searchParams;
    } catch {}
  }

  const getParam = (key: string) => {
    // 1. Buscar en la URL principal (sirve para URL directa)
    const mainValue = params.get(key);

    if (mainValue && mainValue !== "undefined") {
      return mainValue;
    }

    // 2. Si existe adj_redirect, buscar adentro
    if (adjParams) {
      const adjValue = adjParams.get(key);
      if (adjValue && adjValue !== "undefined") {
        return adjValue;
      }
    }

    return null;
  };

  return { getParam };
};

export const buildDeeplink = (
  checkOutElement: DatasetProps,
  orderData: ResponseFetchOrder,
): string => {

  const {
    totalAmount,
    callbackUrl,
    callbackUrlSuccess,
    callbackUrlFail,
    cartId,
    userToken,
  } = checkOutElement;

  const { getParam } = parseFlowUrl(orderData.data.urlFlowRedirect);

  const orderId = getParam("orderId");
  const sellerClientId = getParam("sellerClientId");
  const sellerName = getParam("sellerName") || '';

  const baseWebUrl = `${BASE_URL}/checkout`;

  const url = new URL(baseWebUrl);

  url.searchParams.set("orderId", orderId || '');
  url.searchParams.set("amount", totalAmount);
  url.searchParams.set("callbackUrl", callbackUrl);
  url.searchParams.set("callbackUrlSuccess", callbackUrlSuccess);
  url.searchParams.set("callbackUrlFail", callbackUrlFail);
  url.searchParams.set("sellerClientId", sellerClientId || '');
  url.searchParams.set("sellerName", sellerName);
  url.searchParams.set("cartId", cartId);

  if (userToken) {
    url.searchParams.set("tk", userToken);
  }

  // 🔥 CLAVE: encapsular correctamente el deeplink
  url.searchParams.set(
    "urlFlowRedirect",
    orderData.data.urlFlowRedirect.toString()
  );

  return url.toString();
};

// No se está usando
export const buildDeeplinkApp = (
  checkOutElement: DatasetProps,
  orderData: ResponseFetchOrder,
): string => {
  const { orderId, sellerClientId, storeName } = orderData.data;
  const {
    totalAmount,
    callbackUrl,
    callbackUrlSuccess,
    callbackUrlFail,
    cartId,
  } = checkOutElement;

  const appLink = APP_LINK_ADJUST || APP_SCHEME_BASE || '';
  const base = BASE_URL.replace(/\/$/, '');
  const fallback =
    `${base}/checkout/fallback` +
    `?orderId=${orderId ?? 'null'}` +
    `&amount=${totalAmount}` +
    `&callbackUrl=${encodeURIComponent(callbackUrl!)}` +
    `&callbackUrlSuccess=${encodeURIComponent(callbackUrlSuccess!)}` +
    `&callbackUrlFail=${encodeURIComponent(callbackUrlFail!)}` +
    `&sellerClientId=${sellerClientId ?? ''}` +
    `&sellerName=${encodeURIComponent(storeName)}` +
    `&cartId=${cartId}`;

  const replaced = appLink.replace('adjRedirect', encodeURIComponent(fallback));

  const url =
    `${replaced}` +
    `&orderId=${orderId ?? 'null'}` +
    `&amount=${totalAmount}` +
    `&callbackUrl=${encodeURIComponent(callbackUrl!)}` +
    `&callbackUrlSuccess=${encodeURIComponent(callbackUrlSuccess!)}` +
    `&callbackUrlFail=${encodeURIComponent(callbackUrlFail!)}` +
    `&sellerClientId=${sellerClientId ?? ''}` +
    `&sellerName=${encodeURIComponent(storeName)}` +
    `&cartId=${cartId}`;

  return url;
};

export const switchApiVersion = (
  checkOutElementDataset: DatasetProps,
  setApiVersion: (version: APIVersions) => void
): void => {
  if (checkOutElementDataset.accessToken) {
    setApiVersion("2");
  }
}

export const buildHeaders = (
  checkOutElementDataset: DatasetProps, 
  apiVersion: APIVersions
): HeadersInit => {
  let headers: Record<string, string> = {"Content-Type": "application/json"};
  if(apiVersion === '1' && checkOutElementDataset.apiKey) {
    headers["x-api-key"] = checkOutElementDataset.apiKey;
  } 
  if (apiVersion === '2' && checkOutElementDataset.accessToken) {
    headers["Authorization"] = `Bearer ${checkOutElementDataset.accessToken}`;
  }
  return headers;
}
